/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { accessAPI, formatFecha, TablaFiltrable } from "../../shared";
import { Boton } from "../../shared";

import { Accordion, AccordionTab } from "primereact/accordion";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Menu } from "primereact/menu";
import { confirmDialog } from "primereact/confirmdialog";

const FILTROS = [
  { campo: "empresaExtendida", label: "Empresa extendida", labelPlural: "empresas" }
];
const SEARCH_FIELDS = ["empresaExtendida", "titular"];

export default function SolicitudesExtension({ toast }) {
  const [solicitudes, setSolicitudes] = useState();
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState(null);

  const navigate = useNavigate();
  const menuRefs = useRef({});

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cargar() {
    accessAPI(
      "GET",
      "admin/solicitudextension",
      null,
      (respuesta) => {
        setSolicitudes(respuesta);
        setLoading(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg || "Error al cargar solicitudes de extensión",
          sticky: true
        });
        setLoading(false);
      }
    );
  }

  function codigo(s) {
    return s.certificadoNumero || `#${s.certificadoId}`;
  }

  // Aprobar (da de baja la extensión) o rechazar. Actualiza ambos lados:
  // el backend cambia el estado de la solicitud y, si aprueba, da de baja la
  // extensión (deja de verla el cliente). Acá refrescamos la lista del admin.
  function resolver(s, accion) {
    const esAprobar = accion === "aprobar";
    confirmDialog({
      message: esAprobar
        ? `¿Aprobar la baja de la extensión de ${codigo(s)} hacia "${s.empresaExtendida}"? Se dará de baja la extensión y el cliente dejará de verla.`
        : `¿Rechazar la solicitud de baja de ${codigo(s)} hacia "${s.empresaExtendida}"? La extensión queda como está.`,
      header: esAprobar ? "Aprobar baja de extensión" : "Rechazar solicitud",
      icon: esAprobar ? "pi pi-check" : "pi pi-times",
      acceptLabel: esAprobar ? "Aprobar" : "Rechazar",
      rejectLabel: "Cancelar",
      acceptClassName: esAprobar ? "p-button-danger" : undefined,
      accept: () => {
        setProcesando(s.id);
        accessAPI(
          "PUT",
          `admin/solicitudextension/${s.id}/${accion}`,
          null,
          () => {
            setProcesando(null);
            toast.current?.show({
              severity: "success",
              summary: esAprobar
                ? "Extensión dada de baja"
                : "Solicitud rechazada",
              life: 3000
            });
            cargar();
          },
          (respuesta) => {
            setProcesando(null);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al resolver la solicitud",
              sticky: true
            });
          }
        );
      }
    });
  }

  // Si no hay solicitudes pendientes y terminó de cargar, no mostrar nada
  if (!loading && (!solicitudes || solicitudes.length === 0)) {
    return null;
  }

  if (loading) {
    return (
      <div css={{ marginTop: "1em" }}>
        <Skeleton width="100%" height="3.5rem" borderRadius="6px" />
      </div>
    );
  }

  return (
    <>
      <Accordion
        activeIndex={null}
        css={{
          marginTop: "1em",
          "& .p-accordion-header-link": { color: "black" },
          "& .p-toggleable-content": { transition: "max-height 0.15s ease" }
        }}
      >
        <AccordionTab
          header={`Solicitudes de baja de extensión (${solicitudes.length})`}
        >
          <TablaFiltrable
            value={solicitudes}
            filtros={FILTROS}
            searchFields={SEARCH_FIELDS}
            nombreArchivo="solicitudes_baja_extension"
            emptyMessage="No hay solicitudes pendientes"
          >
            <Column header="Certificado" body={(s) => codigo(s)} />
            <Column header="Titular" body={(s) => s.titular || "-"} />
            <Column
              header="Empresa extendida"
              body={(s) => s.empresaExtendida || "-"}
            />
            <Column
              header="Fecha"
              body={(s) => formatFecha(s.fechaSolicitud) || "-"}
            />
            <Column
              body={(s) => {
                const items = [
                  {
                    label: "Ir al certificado",
                    icon: "pi pi-external-link",
                    command: () =>
                      navigate(
                        `/proceso/${s.procesoId}/certificado/${s.certificadoId}`
                      )
                  },
                  {
                    label: "Aprobar baja",
                    icon: "pi pi-check",
                    command: () => resolver(s, "aprobar")
                  },
                  {
                    label: "Rechazar",
                    icon: "pi pi-times",
                    command: () => resolver(s, "rechazar")
                  }
                ];
                return (
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5em"
                    }}
                  >
                    {procesando === s.id && (
                      <i className="pi pi-spin pi-spinner" />
                    )}
                    <Menu
                      model={items}
                      popup
                      ref={(el) => (menuRefs.current[s.id] = el)}
                      id={`menu_solext_${s.id}`}
                    />
                    <Boton
                      texto
                      negro
                      callback={(e) => menuRefs.current[s.id]?.toggle(e)}
                    >
                      <i className="pi pi-ellipsis-v" />
                    </Boton>
                  </div>
                );
              }}
              style={{ width: "60px", textAlign: "center" }}
            />
          </TablaFiltrable>
        </AccordionTab>
      </Accordion>
    </>
  );
}
